import type { DimensionMode } from "@v2/features/modifiers/base/DimensionSelector";
import type { DimensionModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { useComputedElementDimensions } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import DimensionSelector from "@v2/features/modifiers/base/DimensionSelector";
import { deriveDimensionFromClassName } from "replo-runtime/shared/utils/dimensions";

interface WidthModifierProps {
  onChange: (modifiers: DimensionModifierValue[]) => void;
  excludeOptions?: DimensionMode[];
}

const WidthModifier: React.FC<WidthModifierProps> = ({
  onChange,
  excludeOptions,
}) => {
  const computedElementDimensions = useComputedElementDimensions();
  const extractedWidth = useSemanticModifierValue({
    semanticExtractor: ({ semanticElement, computedStyles }) => {
      const classDerived = deriveDimensionFromClassName(
        semanticElement.cssClasses || undefined,
        "width",
      );
      if (classDerived !== null) {
        return classDerived;
      }
      const widthValue = computedStyles.width;
      if (widthValue === "auto") {
        return "auto" as const;
      }
      if (widthValue === "100%") {
        return "full" as const;
      }
      const match = widthValue?.match(/^(\d+(?:\.\d+)?)/);
      if (match?.[1]) {
        const num = Math.round(Number.parseFloat(match[1]));
        return isNaN(num) ? null : num;
      }
      return null;
    },
  });

  const currentValue = extractedWidth ?? null;

  const handleValueChange = (width: number | "auto" | "full" | null) => {
    if (width !== null) {
      onChange([
        {
          type: "dimension" as const,
          side: "width",
          value: width,
        },
      ]);
    }
  };

  return (
    <div className="width-modifier">
      <DimensionSelector
        type="width"
        value={currentValue}
        onSelect={handleValueChange}
        excludeOptions={excludeOptions}
        computedDimension={computedElementDimensions?.width ?? null}
      />
    </div>
  );
};

export default WidthModifier;
